/**
 * Ensure inline links open in a new tab safely and match doc styling (bold / brand blue via CSS).
 */

export function normalizeAnchorsInHtml(html) {
  if (!html || typeof html !== 'string' || !html.includes('<a')) return html;
  try {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    tpl.content.querySelectorAll('a[href]').forEach((a) => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    return tpl.innerHTML;
  } catch {
    return html;
  }
}

function normalizeBlockData(data) {
  if (!data || typeof data !== 'object') return;

  const htmlStringFields = ['text', 'message', 'html', 'description', 'caption', 'content'];
  for (const key of htmlStringFields) {
    if (key === 'content' && Array.isArray(data.content)) continue;
    if (typeof data[key] === 'string') {
      data[key] = normalizeAnchorsInHtml(data[key]);
    }
  }

  if (Array.isArray(data.items)) {
    data.items = data.items.map((item) => {
      if (typeof item === 'string') return normalizeAnchorsInHtml(item);
      if (item && typeof item === 'object') {
        const next = { ...item };
        if (typeof next.content === 'string') next.content = normalizeAnchorsInHtml(next.content);
        if (typeof next.text === 'string') next.text = normalizeAnchorsInHtml(next.text);
        return next;
      }
      return item;
    });
  }

  // Table: content is rows of cells
  if (Array.isArray(data.content) && data.content.length && data.content.every(Array.isArray)) {
    data.content = data.content.map((row) =>
      row.map((cell) => (typeof cell === 'string' ? normalizeAnchorsInHtml(cell) : cell))
    );
  }

  if (Array.isArray(data.tabs)) {
    data.tabs = data.tabs.map((tab) => {
      if (!tab || typeof tab !== 'object') return tab;
      const t = { ...tab };
      if (typeof t.content === 'string') t.content = normalizeAnchorsInHtml(t.content);
      return t;
    });
  }

  if (Array.isArray(data.columns)) {
    data.columns = data.columns.map((col) => {
      if (!col || typeof col !== 'object') return col;
      const c = { ...col };
      if (typeof c.content === 'string') c.content = normalizeAnchorsInHtml(c.content);
      return c;
    });
  }

  if (Array.isArray(data.steps)) {
    data.steps = data.steps.map((step) => {
      if (!step || typeof step !== 'object') return step;
      const s = { ...step };
      if (typeof s.description === 'string') s.description = normalizeAnchorsInHtml(s.description);
      if (typeof s.content === 'string') s.content = normalizeAnchorsInHtml(s.content);
      return s;
    });
  }
}

export function normalizeLinksInEditorOutput(output) {
  if (!output || !Array.isArray(output.blocks)) return output;
  for (const block of output.blocks) {
    normalizeBlockData(block.data);
  }
  return output;
}

/** Parse Editor.js JSON, normalize <a> tags, re-stringify. No-op if not valid JSON. */
export function normalizeEditorContentJsonString(contentStr) {
  if (!contentStr || typeof contentStr !== 'string') return contentStr;
  try {
    const data = JSON.parse(contentStr);
    normalizeLinksInEditorOutput(data);
    return JSON.stringify(data);
  } catch {
    return contentStr;
  }
}
